import { useState, useEffect, useCallback } from 'react';
import type { SandboxProfile, SandboxAuth } from '../types/sandbox';
import { supabase } from '../lib/supabase';
import { getMockProfile, MOCK_PROFILES } from '../lib/mock-data';

const STORAGE_KEY = 'symsvox_sandbox_did';

export function useSandboxAuth(): SandboxAuth & {
  login: (did: string) => void;
  loginWithUsername: (username: string) => Promise<void>;
  logout: () => void;
  register: (username: string, displayName: string) => Promise<SandboxProfile>;
} {
  const [did, setDid] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEY) || '';
  });
  const [profile, setProfile] = useState<SandboxProfile | null>(null);

  // Load profile when DID changes
  useEffect(() => {
    if (!did) {
      setProfile(null);
      return;
    }

    const loadProfile = async () => {
      if (supabase) {
        const { data } = await supabase
          .from('sandbox_profiles')
          .select('*')
          .eq('operator_did', did)
          .single();
        if (data) setProfile(data as SandboxProfile);
      } else {
        // Mock mode
        const mock = getMockProfile(did);
        if (mock) setProfile(mock);
      }
    };

    loadProfile();
  }, [did]);

  const login = useCallback((newDid: string) => {
    localStorage.setItem(STORAGE_KEY, newDid);
    setDid(newDid);
  }, []);

  const loginWithUsername = useCallback(async (username: string) => {
    if (supabase) {
      const { data } = await supabase
        .from('sandbox_profiles')
        .select('*')
        .eq('username', username)
        .single();
      if (data) {
        const p = data as SandboxProfile;
        login(p.operator_did);
        setProfile(p);
      }
    } else {
      const mock = MOCK_PROFILES.find(p => p.username === username);
      if (mock) {
        login(mock.operator_did);
        setProfile(mock);
      }
    }
  }, [login]);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setDid('');
    setProfile(null);
  }, []);

  const register = useCallback(async (username: string, displayName: string): Promise<SandboxProfile> => {
    const newDid = `did:sym:operator:${username}-${Date.now().toString(36)}`;

    if (supabase) {
      const { data, error } = await supabase
        .from('sandbox_profiles')
        .insert({
          operator_did: newDid,
          username,
          display_name: displayName,
          theme_config: { preset: 'default' },
        })
        .select()
        .single();
      if (error) throw new Error(error.message);
      const p = data as SandboxProfile;
      login(newDid);
      setProfile(p);
      return p;
    }

    // Mock mode
    const mockProfile: SandboxProfile = {
      id: crypto.randomUUID(),
      operator_did: newDid,
      username,
      display_name: displayName,
      bio: null,
      avatar_url: null,
      theme_config: { preset: 'default' },
      custom_css: null,
      is_premium: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    login(newDid);
    setProfile(mockProfile);
    return mockProfile;
  }, [login]);

  return {
    did,
    profile,
    isAuthenticated: !!did && !!profile,
    login,
    loginWithUsername,
    logout,
    register,
  };
}
